import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Modal } from '@/components/ui'
import { useSetEndDates } from '@/hooks/insights'
import { booksMissingEndDate, type LibraryBook, suggestEndDate } from '@/lib/insights'
import { todayISO } from '@/lib/utils'

/** Warns about read books without an end date and lets the user fill them in one go */
export function MissingDatesBanner({ books }: { books: LibraryBook[] }) {
  const { t } = useTranslation()
  const missing = useMemo(() => booksMissingEndDate(books), [books])
  const [isOpen, setIsOpen] = useState(false)

  if (missing.length === 0) return null

  return (
    <>
      <div className="mb-6 flex flex-col gap-3 rounded-lg border border-warning/30 bg-warning/10 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="font-medium text-foreground">
            {t('insights.missingDates.title', { count: missing.length })}
          </p>
          <p className="text-sm text-muted-foreground">{t('insights.missingDates.description')}</p>
        </div>
        <Button size="sm" variant="warning" onClick={() => setIsOpen(true)} className="shrink-0">
          {t('insights.missingDates.fix')}
        </Button>
      </div>

      {isOpen && <MissingDatesModal books={missing} onClose={() => setIsOpen(false)} />}
    </>
  )
}

function MissingDatesModal({ books, onClose }: { books: LibraryBook[]; onClose: () => void }) {
  const { t } = useTranslation()
  const setEndDates = useSetEndDates()
  const [dates, setDates] = useState<Record<string, string>>(() =>
    Object.fromEntries(books.map((b) => [b.id, suggestEndDate(b)]))
  )

  const handleSave = async () => {
    const updates = Object.entries(dates)
      .filter(([, endDate]) => !!endDate)
      .map(([bookId, endDate]) => ({ bookId, endDate }))
    await setEndDates.mutateAsync(updates)
    onClose()
  }

  return (
    <Modal isOpen onClose={onClose} title={t('insights.missingDates.modalTitle')} size="lg">
      <div className="flex flex-col gap-4 overflow-y-auto p-6">
        <p className="text-sm text-muted-foreground">{t('insights.missingDates.modalHint')}</p>

        <ul className="divide-y divide-border">
          {books.map((book) => (
            <li key={book.id} className="flex items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="truncate font-medium text-foreground">{book.title}</p>
                <p className="truncate text-sm text-muted-foreground">{book.author}</p>
              </div>
              <input
                type="date"
                aria-label={t('books.fields.endDate')}
                value={dates[book.id] ?? ''}
                max={todayISO()}
                min={book.start_date ?? undefined}
                onChange={(e) => setDates((prev) => ({ ...prev, [book.id]: e.target.value }))}
                className="shrink-0 rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </li>
          ))}
        </ul>

        {setEndDates.isError && <p className="text-sm text-error">{t('errors.general')}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={setEndDates.isPending}>
            {setEndDates.isPending ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
